import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { AgentBinary, DownloadFile, DownloadsListing, listDownloads } from './api';
import { formatAt, formatBytes } from './format';

/** While any installer is still being hashed, ask again this often. */
export const HASH_POLL_MS = 30_000;

/** The VPS folder nginx lists at /downloads/ — shown until the broker answers. */
const DEFAULT_DIR = '/root/tunnel-artifacts/downloads/';

/** File-name prefix → the product name an operator recognises. */
const PRODUCTS: Record<string, string> = {
  DikonekWeigherSetup: 'Weigher',
  DikonekMESSetup: 'MES',
  EskolWeigherSetup: 'Weigher (Eskol)',
  DikonekWatcherReportSetup: 'Watcher Report',
};

/**
 * `DikonekWeigherSetup-1.6.1.exe` → { product: 'Weigher', version: '1.6.1' }.
 * The version is the `-<semver>` segment before the extension; an unknown
 * prefix is shown as-is, and a name without a semver has no version.
 */
export function parseInstallerName(name: string): { product: string; version: string | null } {
  // Drop a letter-led extension (.exe, .zip, .msi) — never a `.1` of a version.
  const base = name.replace(/\.[A-Za-z][A-Za-z0-9]*$/, '');
  const m = /^(.+?)-(\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?)$/.exec(base);
  const prefix = m ? m[1] : (/^(.+?)-\d/.exec(base)?.[1] ?? base);
  return { product: PRODUCTS[prefix] ?? prefix, version: m ? m[2] : null };
}

/** React Query refetchInterval: poll only while a hash is still computing. */
export function downloadsRefetchInterval(data: DownloadsListing | undefined): number | false {
  return data?.files.some((f) => f.sha256 === null) ? HASH_POLL_MS : false;
}

function copy(value: string): void {
  // The clipboard API is absent on plain http and may reject without a user
  // gesture; a failed copy is not worth an error banner.
  void Promise.resolve(navigator.clipboard?.writeText(value)).catch(() => undefined);
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied' : label}>
      <IconButton
        size="small"
        aria-label={label}
        onClick={() => {
          copy(value);
          setCopied(true);
        }}
      >
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}

/** First 12 hex chars (full value on hover) + a copy button for the full hash. */
function ShaCell({ sha256, of }: { sha256: string | null; of: string }) {
  if (!sha256) {
    return (
      <Typography variant="body2" color="text.secondary" component="span">
        computing…
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="code" title={sha256} sx={{ fontFamily: 'monospace' }}>
        {sha256.slice(0, 12)}
      </Box>
      <CopyButton value={sha256} label={`Copy SHA-256 of ${of}`} />
    </Box>
  );
}

function PublishedCell({ iso }: { iso: string }) {
  return (
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      <span title={iso}>{formatAt(iso)}</span>
    </TableCell>
  );
}

function newestFirst(files: DownloadFile[]): DownloadFile[] {
  return [...files].sort((a, b) => Date.parse(b.modifiedAt) - Date.parse(a.modifiedAt));
}

const AGENT_ROWS: Array<{ platform: 'win32' | 'linux'; label: string }> = [
  { platform: 'win32', label: 'Windows agent (exe)' },
  { platform: 'linux', label: 'Linux agent bundle' },
];

function AgentBinariesTable({ agent }: { agent: DownloadsListing['agent'] }) {
  return (
    <Paper elevation={1}>
      <TableContainer>
        <Table size="small" aria-label="Agent binaries">
          <TableHead>
            <TableRow>
              <TableCell>Binary</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>File</TableCell>
              <TableCell align="right">Size</TableCell>
              <TableCell>Published</TableCell>
              <TableCell>SHA-256</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {AGENT_ROWS.map(({ platform, label }) => {
              const a: AgentBinary | undefined = agent[platform];
              if (!a) {
                return (
                  <TableRow key={platform}>
                    <TableCell>{label}</TableCell>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary" component="span">
                        Not served by this broker.
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              }
              return (
                <TableRow key={platform} hover>
                  <TableCell>{label}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{a.version}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    <Link href={a.url}>{a.url}</Link>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {formatBytes(a.sizeBytes)}
                  </TableCell>
                  <PublishedCell iso={a.modifiedAt} />
                  <TableCell>
                    <ShaCell sha256={a.sha256} of={label} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

/**
 * "Downloads": everything tunnel.dikonek.com publishes — the installers in the
 * public /downloads/ folder and the agent binaries — so the owner no longer
 * needs the raw nginx listing. Read-only: publishing is an SFTP step
 * (apps/tunnel/README.md, "Downloads tab"); there is no upload or delete here.
 */
export function DownloadsTable() {
  const downloads = useQuery({
    queryKey: ['downloads'],
    queryFn: listDownloads,
    // The broker hashes big installers in the background and answers null
    // meanwhile; poll only until every hash has landed.
    refetchInterval: (query) => downloadsRefetchInterval(query.state.data),
  });
  const data = downloads.data;
  const publicUrl = window.location.origin + (data?.publicBase ?? '/downloads/');
  const files = data ? newestFirst(data.files) : [];

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }} data-testid="downloads">
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="h2">
            Downloads
          </Typography>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                aria-label="refresh downloads"
                disabled={downloads.isFetching}
                onClick={() => void downloads.refetch()}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Link href={publicUrl} target="_blank" rel="noopener" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {publicUrl}
          </Link>
          <CopyButton value={publicUrl} label="Copy the public downloads URL" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Publish: SFTP into <code>{data?.dir ?? DEFAULT_DIR}</code> (see apps/tunnel/README.md,
          &quot;Downloads tab&quot;). This list is read-only.
        </Typography>
      </Box>

      {downloads.isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', p: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : downloads.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void downloads.refetch()}>
              Retry
            </Button>
          }
        >
          {downloads.error instanceof Error ? downloads.error.message : 'could not load downloads'}
        </Alert>
      ) : (
        <>
          {files.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              No installers published yet.
            </Typography>
          ) : (
            <Paper elevation={1}>
              <TableContainer>
                <Table size="small" aria-label="Installers">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Version</TableCell>
                      <TableCell>File</TableCell>
                      <TableCell align="right">Size</TableCell>
                      <TableCell>Published</TableCell>
                      <TableCell>SHA-256</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {files.map((f) => {
                      const { product, version } = parseInstallerName(f.name);
                      return (
                        <TableRow key={f.name} hover>
                          <TableCell>{product}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{version ?? '—'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Link href={f.url}>{f.name}</Link>
                              <CopyButton
                                value={window.location.origin + f.url}
                                label={`Copy the link to ${f.name}`}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {formatBytes(f.sizeBytes)}
                          </TableCell>
                          <PublishedCell iso={f.modifiedAt} />
                          <TableCell>
                            <ShaCell sha256={f.sha256} of={f.name} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Typography variant="subtitle1" component="h3" sx={{ mt: 1 }}>
            Agent binaries
          </Typography>
          <AgentBinariesTable agent={data?.agent ?? {}} />
        </>
      )}
    </Box>
  );
}
